// Geometrische schaduwprojectie op grondniveau (z = 0).
//
// Voor elk blok met heightTop > 0 nemen we de bovenrand-polygoon (op
// hoogte heightTop) en projecteren elk hoekpunt langs de tegen-zon-vector
// tot het grondvlak. De resulterende grond-polygoon is de slagschaduw
// die het blok werpt.
//
// Voor blokken met heightBottom > 0 (bv. boombladdek) is de schaduw die
// op grondniveau valt minder dramatisch: de onderzijde laat zonlicht
// onder het bladdek door. Voor v1 negeren we dat detail en projecteren
// we de bovenrand op grondniveau — dit is een licht-conservatieve schatting
// (iets te veel schaduw onder het bladdek). De ondoorlatendheid wordt wel
// verrekend via `transmission`.
//
// Coördinaten: blokken zijn opgeslagen als [lng, lat] in WGS84. We projecteren
// naar lokale meters t.o.v. een referentiepunt (eerste blok-vertex) via een
// equirectangular benadering — voldoende nauwkeurig voor onze 200 m schaal.
//
// Onder de horizonbouwgrens van 10° (§3.2) tellen we geen zonlicht: de
// projectie zou dan absurd lange schaduwen produceren (lengte = h / tan(elev)
// loopt tegen oneindig).

import type { Block } from "../types/scenario";
import { sunDirectionEnu, type SunPosition } from "./sunPosition";

/** Minimale zon-elevatie waarbij we überhaupt schaduw rekenen (radialen). */
export const MIN_ELEVATION_RAD = (10 * Math.PI) / 180;

export interface ShadowFeature {
  /** Polygoon op grondniveau in [lng, lat]. */
  polygon: [number, number][];
  blockId: string;
  /**
   * 0–1: aandeel licht dat door het blok wordt geblokkeerd. 1 = volledig
   * ondoorlatend (gebouw); 0,6 = boombladdek met 40% transmissie.
   */
  blockingFactor: number;
}

const EARTH_R = 6_378_137; // m, WGS84 equatorial radius

interface LocalProj {
  refLat: number;
  refLng: number;
  /** meter per graad latitude (≈ constant). */
  mPerLat: number;
  /** meter per graad longitude (afhankelijk van breedtegraad). */
  mPerLng: number;
}

function makeProjection(refLat: number, refLng: number): LocalProj {
  const mPerLat = (Math.PI * EARTH_R) / 180;
  const mPerLng = mPerLat * Math.cos((refLat * Math.PI) / 180);
  return { refLat, refLng, mPerLat, mPerLng };
}

function lngLatToMeters(p: [number, number], proj: LocalProj): { x: number; y: number } {
  return {
    x: (p[0] - proj.refLng) * proj.mPerLng,
    y: (p[1] - proj.refLat) * proj.mPerLat,
  };
}

function metersToLngLat(m: { x: number; y: number }, proj: LocalProj): [number, number] {
  return [proj.refLng + m.x / proj.mPerLng, proj.refLat + m.y / proj.mPerLat];
}

/**
 * Bereken de slagschaduwen op grondniveau voor een collectie blokken bij
 * een gegeven zonpositie. Geeft één feature per schaduwwerpend blok.
 *
 * Onder MIN_ELEVATION_RAD geeft de functie een lege array.
 */
export function computeShadows(blocks: Block[], sun: SunPosition): ShadowFeature[] {
  return computeShadowsAt(blocks, sun, 0);
}

/**
 * Generieke variant: bereken slagschaduwen op een horizontaal meetvlak op
 * hoogte `targetZ` (in meters boven maaiveld).
 *
 * Voor `targetZ = 0` is het resultaat identiek aan `computeShadows`. Voor
 * dakvlakken (targetZ = heightTop van een gebouw) wordt voor elk schaduw-
 * werpend blok de projectie van zowel de boven- als onderzijde meegenomen.
 *
 * Wiskundig: een blok met footprint P en hoogtebereik [hb, ht] werpt op
 * plane z=tz schaduw op het gebied dat door de zon-vector geblokkeerd
 * wordt. Voor hb < tz < ht kan een deel van het blok onder het meetvlak
 * zitten (bv. een gebouw vlakbij waar het meetvlak de dakhoogte van een
 * lager gebouw is). We rekenen alleen het bovenliggende deel mee:
 * effectieve hoogtegrenzen [max(tz, hb), ht]. Onder die grenzen draagt
 * niets bij aan de schaduw op het meetvlak.
 */
export function computeShadowsAt(
  blocks: Block[],
  sun: SunPosition,
  targetZ: number,
): ShadowFeature[] {
  if (sun.altitude < MIN_ELEVATION_RAD) return [];
  if (blocks.length === 0) return [];

  // Referentiepunt voor de lokale projectie: het zwaartepunt van alle vertices.
  // Centreren beperkt de equirectangular-fout aan de randen van het scenario.
  let sumLng = 0;
  let sumLat = 0;
  let count = 0;
  for (const b of blocks) {
    for (const p of b.polygon) {
      sumLng += p[0];
      sumLat += p[1];
      count++;
    }
  }
  if (count === 0) return [];
  const proj = makeProjection(sumLat / count, sumLng / count);

  const sunDir = sunDirectionEnu(sun);
  // Schaduw verschuift per meter hoogte boven targetZ met deze offset.
  const dxPerM = -sunDir.x / sunDir.z;
  const dyPerM = -sunDir.y / sunDir.z;

  const shadows: ShadowFeature[] = [];
  for (const b of blocks) {
    if (b.heightTop <= 0) continue;
    const effectiveBottom = Math.max(targetZ, b.heightBottom);
    const effectiveTop = b.heightTop;
    if (effectiveTop <= effectiveBottom) continue; // blok zit (deels) onder meetvlak

    const transmission = (b.lightTransmission ?? 0) / 100;
    const blockingFactor = Math.max(0, Math.min(1, 1 - transmission));
    if (blockingFactor <= 0) continue;

    // Bouw de schaduwpolygoon: de convexe hull van het blok-footprint
    // geprojecteerd vanaf de effectieve onder- én bovenrand naar het meetvlak.
    // De projectie-offset per meter is (dxPerM, dyPerM); we passen die toe
    // op (effectieveBottom − targetZ) en (effectieveTop − targetZ).
    const offBottom = effectiveBottom - targetZ;
    const offTop = effectiveTop - targetZ;
    const pts: { x: number; y: number }[] = [];
    for (const v of b.polygon) {
      const m = lngLatToMeters(v, proj);
      pts.push({ x: m.x + offBottom * dxPerM, y: m.y + offBottom * dyPerM });
      pts.push({ x: m.x + offTop * dxPerM, y: m.y + offTop * dyPerM });
    }
    const hull = convexHull(pts);
    if (hull.length < 3) continue;
    shadows.push({
      polygon: hull.map((p) => metersToLngLat(p, proj)),
      blockId: b.id,
      blockingFactor,
    });
  }
  return shadows;
}

// Andrew's monotone chain — convexe hull in O(n log n). Verwacht punten in
// een 2D vlak (meters). Tegenklokwijze (CCW) volgorde.
function convexHull(points: { x: number; y: number }[]): { x: number; y: number }[] {
  if (points.length < 3) return points.slice();
  const sorted = points
    .slice()
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));
  // Verwijder duplicates op basis van een kleine tolerantie.
  const dedup: { x: number; y: number }[] = [];
  for (const p of sorted) {
    const last = dedup[dedup.length - 1];
    if (!last || Math.abs(last.x - p.x) > 1e-6 || Math.abs(last.y - p.y) > 1e-6) {
      dedup.push(p);
    }
  }
  if (dedup.length < 3) return dedup;

  const cross = (
    o: { x: number; y: number },
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): number => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower: { x: number; y: number }[] = [];
  for (const p of dedup) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: { x: number; y: number }[] = [];
  for (let i = dedup.length - 1; i >= 0; i--) {
    const p = dedup[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}
